/**
 * Step1_Intent.js
 *
 * Collects: intent (BUY / RENT / PRESALE / COLAB), property_type, usage.
 * On "Next": calls createDraft (POST /api/ms/requests/) → returns UUID passed to wizard shell.
 */

import { useState, useEffect } from 'react';
import { api, apiRoutes } from '../../../api';
import { createDraft } from '../services/propertyRequestService';
import CompleteProfileModal from '../components/CompleteProfileModal';

/* ── Intent options ─────────────────────────────────────────── */
const INTENTS = [
    {
        value: 'BUY',
        label: 'خرید',
        desc: 'خرید ملک به صورت قطعی',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
                <path d="M9 21V12h6v9" />
            </svg>
        ),
        color: '#1565c0',
        bg: '#e3f2fd',
        selectedBg: '#bbdefb',
        border: '#1565c0',
    },
    {
        value: 'RENT',
        label: 'اجاره',
        desc: 'اجاره ماهانه یا سالانه',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                <line x1="12" y1="12" x2="12" y2="16" />
                <line x1="10" y1="14" x2="14" y2="14" />
            </svg>
        ),
        color: '#2e7d32',
        bg: '#e8f5e9',
        selectedBg: '#c8e6c9',
        border: '#2e7d32',
    },
    {
        value: 'PRESALE',
        label: 'پیش‌خرید',
        desc: 'خرید ملک در مرحله ساخت',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
        ),
        color: '#e65100',
        bg: '#fff3e0',
        selectedBg: '#ffe0b2',
        border: '#e65100',
    },
    {
        value: 'COLAB',
        label: 'مشارکت',
        desc: 'مشارکت در ساخت',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        ),
        color: '#6a1b9a',
        bg: '#f3e5f5',
        selectedBg: '#e1bee7',
        border: '#6a1b9a',
    },
];

export default function Step1_Intent({ onNext, initialData = {} }) {
    const [intent, setIntent] = useState(initialData.intent || '');
    const [propertyTypeId, setPropertyTypeId] = useState(initialData.property_type || '');
    const [usageId, setUsageId] = useState(initialData.usage || '');

    const [propertyTypes, setPropertyTypes] = useState([]);
    const [usages, setUsages] = useState([]);
    const [loadingTypes, setLoadingTypes] = useState(false);
    const [loadingUsages, setLoadingUsages] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [missingFields, setMissingFields] = useState(null); // null = no modal
    // store the payload so we can retry after profile is completed
    const [pendingPayload, setPendingPayload] = useState(null);

    /* fetch property types */
    useEffect(() => {
        setLoadingTypes(true);
        api.get(apiRoutes.properties.propertyTypes)
            .then(r => setPropertyTypes(r.data))
            .catch(() => {})
            .finally(() => setLoadingTypes(false));
    }, []);

    /* fetch usages */
    useEffect(() => {
        setLoadingUsages(true);
        api.get(apiRoutes.properties.usages)
            .then(r => setUsages(r.data))
            .catch(() => {})
            .finally(() => setLoadingUsages(false));
    }, []);

    const canProceed = !!intent;

    const attemptCreate = async (payload) => {
        const draft = await createDraft(payload);
        onNext(draft.id, { intent: payload.intent, property_type: payload.property_type || '', usage: payload.usage || '' });
    };

    const handleNext = async () => {
        if (!canProceed) return;
        setError('');
        setSubmitting(true);
        const payload = { intent };
        if (propertyTypeId) payload.property_type = propertyTypeId;
        if (usageId) payload.usage = usageId;

        try {
            await attemptCreate(payload);
        } catch (err) {
            const data = err?.response?.data;
            // 403 with missing_fields → show inline profile completion modal
            if (err?.response?.status === 403 && data?.missing_fields?.length) {
                setPendingPayload(payload);
                setMissingFields(data.missing_fields);
            } else {
                setError('خطا در ایجاد درخواست. لطفاً دوباره تلاش کنید.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Called by CompleteProfileModal after a successful PATCH
    const handleProfileSaved = async () => {
        setMissingFields(null);
        setError('');
        setSubmitting(true);
        try {
            await attemptCreate(pendingPayload);
        } catch (err) {
            const data = err?.response?.data;
            // Still missing something (e.g. phone_verified) — show notice
            if (err?.response?.status === 403 && data?.missing_fields?.length) {
                setMissingFields(data.missing_fields);
            } else {
                setError('خطا در ایجاد درخواست. لطفاً دوباره تلاش کنید.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            {/* ── Profile completion modal (shown on 403) ── */}
            {missingFields && (
                <CompleteProfileModal
                    missingFields={missingFields}
                    onSuccess={handleProfileSaved}
                    onClose={() => setMissingFields(null)}
                />
            )}

            {/* ── Title ── */}
            <h2 className="prw-step-title">هدف شما از جستجو چیست؟</h2>
            <p className="prw-step-desc">
                نوع معامله‌ای که دنبال آن هستید را انتخاب کنید.
            </p>

            {/* ── Intent cards ── */}
            <div className="prw-option-grid s1-intent-grid">
                {INTENTS.map(opt => {
                    const active = intent === opt.value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            className={`prw-option-card s1-intent-card ${active ? 'selected' : ''}`}
                            style={active ? {
                                borderColor: opt.border,
                                background: opt.selectedBg,
                                color: opt.color,
                            } : {}}
                            onClick={() => setIntent(opt.value)}
                            aria-pressed={active}
                        >
                            <span className="s1-intent-icon" style={{ color: active ? opt.color : undefined }}>
                                {opt.icon}
                            </span>
                            <span className="s1-intent-label">{opt.label}</span>
                            <span className="s1-intent-desc">{opt.desc}</span>
                        </button>
                    );
                })}
            </div>

            {/* ── Optional refinements ── */}
            <div className="s1-optional">
                <p className="s1-optional-title">جزئیات بیشتر <span>(اختیاری)</span></p>

                <div className="s1-selects-row">
                    {/* Property type */}
                    <div className="prw-field">
                        <label className="prw-label" htmlFor="s1-prop-type">نوع ملک</label>
                        <select
                            id="s1-prop-type"
                            className="prw-select"
                            value={propertyTypeId}
                            onChange={e => setPropertyTypeId(e.target.value)}
                            disabled={loadingTypes}
                        >
                            <option value="">{loadingTypes ? 'در حال بارگذاری...' : 'همه انواع'}</option>
                            {propertyTypes.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Usage */}
                    <div className="prw-field">
                        <label className="prw-label" htmlFor="s1-usage">کاربری</label>
                        <select
                            id="s1-usage"
                            className="prw-select"
                            value={usageId}
                            onChange={e => setUsageId(e.target.value)}
                            disabled={loadingUsages}
                        >
                            <option value="">{loadingUsages ? 'در حال بارگذاری...' : 'همه کاربری‌ها'}</option>
                            {usages.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* ── Error ── */}
            {error && <p className="prw-error">{error}</p>}

            {/* ── Nav ── */}
            <div className="prw-nav">
                <div /> {/* no back on step 1 */}
                <button
                    className="prw-btn prw-btn-primary"
                    onClick={handleNext}
                    disabled={!canProceed || submitting}
                >
                    {submitting ? 'در حال ثبت...' : 'مرحله بعد ←'}
                </button>
            </div>
        </div>
    );
}
